import { useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import styles from './MarketHealth.module.css';
import { ZONE_COLORS } from './riskScale';
import { toSortedPoints } from './chartDateUtils';
import { baseChartOptions, baseXAxis, baseYAxis, RANGE_PRESETS, WEEKLY_DATA_GROUPING } from './chartTheme';
import { selectLag } from './strategyLagSelect';
import { MODE_HISTORY_FIELD } from './strategyTransitions';
import { computeWindowStats, buildDrawdownStatRows } from './strategyStats';
import PerformanceStatsTable from './PerformanceStatsTable';


// Below this many points there's nothing meaningful to draw an underwater
// plot from — mirrors StrategyChart's own bootstrap threshold (spec §10).
const MIN_POINTS_FOR_CHART = 2;

// Custom range-preset buttons (spec §8) now come from chartTheme.js's shared
// RANGE_PRESETS (previously duplicated here — de-duplicated per the spec's
// note that a human would fold the two copies together once both landed).
// The shared list includes the deep-crash windows (Dot-com, GFC) this
// underwater plot most wants to surface.

// Compute drawdown series from an equity/price series (spec §6): for each
// point, the % decline from the running peak seen so far. Peak-tracking is
// monotonically non-decreasing, so the output is always <= 0 (0 exactly at
// a new high-water mark).
export const computeDrawdown = (equitySeries) => {
  let peak = -Infinity;
  return equitySeries.map(([ts, value]) => {
    peak = Math.max(peak, value);
    const dd = ((value - peak) / peak) * 100; // percentage
    return [ts, dd];
  });
};

// Builds Highcharts `zones` (zoneAxis: 'x') coloring the strategy drawdown
// line green while invested, red while in cash — identical idiom to
// StrategyChart's `buildPositionZones` (duplicated here for the same reason
// as MODE_HISTORY_FIELD/RANGE_PRESETS above).
const buildPositionZones = (points, modeField, lag) => {
  if (points.length === 0) return undefined;
  const zones = [];
  let currentPosition = selectLag(points[0][modeField], lag)?.position;
  for (let i = 1; i < points.length; i++) {
    const position = selectLag(points[i][modeField], lag)?.position;
    if (position !== currentPosition) {
      zones.push({
        value: points[i].ts - 1,
        color: currentPosition === 'invested' ? ZONE_COLORS.green : ZONE_COLORS.red,
      });
      currentPosition = position;
    }
  }
  zones.push({ color: currentPosition === 'invested' ? ZONE_COLORS.green : ZONE_COLORS.red });
  return zones;
};

const formatDelta = (delta) => (Number.isFinite(delta) ? `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}pp` : '—');

/**
 * "Underwater plot" (spec §6): drawdown from the running peak, for both the
 * selected strategy mode and SPY buy-and-hold, on the same time axis — the
 * product's strongest evidence, visualized. `mode` ('responsive'|'patient')
 * selects which per-entry field to read, same convention as StrategyChart.
 *
 * Uses Highcharts Stock, but — per spec §8 — with the native `rangeSelector`
 * disabled in favor of a custom button row that jumps the x-axis to fixed
 * historical windows (2018 Vol / 2020 COVID / 2022 Bear) as well as All/5Y.
 */
const DrawdownChart = ({ history = [], strategy, mode, lag = false }) => {
  const modeField = MODE_HISTORY_FIELD[mode] || MODE_HISTORY_FIELD.adaptive;
  // Populated via HighchartsReact's `callback` prop (invoked with the chart
  // instance once Highcharts constructs it) rather than a forwarded `ref` —
  // simpler than the ref+useImperativeHandle dance and doesn't require the
  // test's HighchartsReact mock to emulate `React.forwardRef`.
  const chartRef = useRef(null);
  const [selectedPreset, setSelectedPreset] = useState(0);

  const sorted = useMemo(() => toSortedPoints(history, 'spy'), [history]);

  // Only points with BOTH a SPY price and the selected mode's strategy
  // equity are usable — keeps the two drawdown series (and the shared
  // tooltip's index lookup) aligned point-for-point.
  const pairedAll = useMemo(
    () => sorted.filter((d) => typeof d.spy === 'number' && typeof selectLag(d[modeField], lag)?.equity === 'number'),
    [sorted, modeField, lag],
  );

  // Crash presets (RANGE_PRESETS with `subset: true`) FILTER the data to the
  // episode window rather than just zooming, so the navigator spans only the
  // crash and drawdown is measured from the WINDOW's starting peak (0% at the
  // window start) — i.e. "reinitialized for that period" — instead of from an
  // all-time high years earlier. 'All'/'Trailing NY' keep the full dataset.
  const activePreset = RANGE_PRESETS[selectedPreset];
  const paired = useMemo(() => {
    if (!activePreset?.subset) return pairedAll;
    const [min, max] = activePreset.getExtremes();
    return pairedAll.filter((d) => d.ts >= min && d.ts <= max);
  }, [pairedAll, activePreset]);

  if (paired.length === 0) {
    return (
      <p className={styles.statusMessage}>
        Drawdown history begins once the first daily score is computed. Check back after
        the next daily run.
      </p>
    );
  }

  if (paired.length < MIN_POINTS_FOR_CHART) {
    return (
      <p className={styles.statusMessage}>
        Only one day of history so far ({paired[0].date}) — the drawdown chart needs at
        least two days of data. Check back tomorrow.
      </p>
    );
  }

  const strategyDrawdown = computeDrawdown(paired.map((d) => [d.ts, selectLag(d[modeField], lag).equity]));
  const spyDrawdown = computeDrawdown(paired.map((d) => [d.ts, d.spy]));
  const zones = buildPositionZones(paired, modeField, lag);

  // Drawdown-focused stats for the active preset (the "picker") — Max
  // Drawdown, the drawdown-gap Δ vs SPY, the drawdown-weighted Martin Ratio,
  // and Time Invested (see buildDrawdownStatRows), not the return/CAGR set the
  // Strategy cards lead with.
  //
  // These come from the backend's PRECOMPUTED per-preset windows
  // (strategy[mode][lag].windows[presetKey]), which are computed over the FULL
  // DAILY series — so the numbers are daily-accurate for every range preset.
  // The chart above still draws the weekly-downsampled `paired` series; only
  // the stats TABLE reads the precomputed daily windows. Falls back to
  // recomputing from the weekly `paired` slice for older payloads that predate
  // the `.windows` field (or when strategy is unavailable), so nothing breaks.
  const windowStats = strategy?.[mode]?.[lag ? 'lagOn' : 'lagOff']?.windows?.[activePreset?.key];
  const drawdownStatRows = buildDrawdownStatRows(
    windowStats ?? computeWindowStats(paired, modeField, lag),
  );

  // dataGrouping (weekly-and-coarser as you zoom out) collapses the daily
  // series into fewer drawn points. The tooltip reads drawdown values off the
  // hovered points directly (grouping-safe), and only uses this timestamp map
  // to recover the exact calendar date for ungrouped (daily) views.
  const rowByTs = new Map(paired.map((d) => [d.ts, d]));

  const handlePresetClick = (preset, index) => {
    setSelectedPreset(index);
    const xAxis = chartRef.current?.xAxis?.[0];
    if (!xAxis) return;
    if (preset.subset) {
      // Data is filtered to the window on re-render (see `paired`), so clear
      // any leftover extremes from a previous relative preset and let the
      // whole (now-subset) series show.
      xAxis.setExtremes(undefined, undefined);
      return;
    }
    const [min, max] = preset.getExtremes();
    xAxis.setExtremes(min ?? undefined, max ?? undefined);
  };

  const chartOptions = {
    ...baseChartOptions,
    rangeSelector: { enabled: false },
    navigator: { enabled: true },
    scrollbar: { enabled: false },
    xAxis: { ...baseXAxis },
    yAxis: {
      ...baseYAxis,
      max: 0,
      title: { text: undefined },
      labels: { ...baseYAxis.labels, format: '{value}%' },
    },
    legend: { ...baseChartOptions.legend, enabled: true },
    tooltip: {
      ...baseChartOptions.tooltip,
      shared: true,
      xDateFormat: '%Y-%m-%d',
      formatter() {
        // Read the drawdown values straight off the hovered points Highcharts
        // hands the shared tooltip, keyed by series name. The old approach
        // keyed the ts-maps off `this.x` on the assumption that — because both
        // series group by 'low' — the bucket timestamp always equalled a raw
        // daily key. That doesn't hold: with dataGrouping active the two
        // series can bottom out on different days within a bucket, and `this.x`
        // is the bucket's start timestamp, which often isn't a daily key at
        // all — so the map lookups missed and the tooltip showed '—'. The
        // point `y` values are always present, grouping or not.
        const byName = new Map((this.points ?? []).map((p) => [p.series.name, p]));
        const strategyPoint = byName.get('Market Health Strategy');
        const spyPoint = byName.get('Buy & Hold SPY');
        const strategyDd = strategyPoint?.y;
        const spyDd = spyPoint?.y;
        // Prefer the hovered point's own timestamp for the date label; fall
        // back to `this.x`. `rowByTs` may still hit for ungrouped (daily) views
        // to show the exact calendar date, otherwise format the timestamp.
        const ts = this.points?.[0]?.point?.x ?? this.x;
        const dateLabel = rowByTs.get(ts)?.date ?? Highcharts.dateFormat('%Y-%m-%d', ts);
        const delta = Number.isFinite(strategyDd) && Number.isFinite(spyDd) ? strategyDd - spyDd : NaN;
        return [
          `<b>${dateLabel}</b>`,
          `Strategy drawdown: ${Number.isFinite(strategyDd) ? strategyDd.toFixed(1) : '—'}%`,
          `SPY drawdown: ${Number.isFinite(spyDd) ? spyDd.toFixed(1) : '—'}%`,
          `Δ vs SPY: ${formatDelta(delta)}`,
        ].join('<br/>');
      },
    },
    plotOptions: {
      // Drawdowns are <= 0, so the "worst" value in a group is its MINIMUM —
      // group by 'low' so a weekly/monthly bucket surfaces the deepest day of
      // the decline rather than averaging the trough away. (Overrides the
      // shared WEEKLY_DATA_GROUPING default of 'high', which suits rising
      // price/score lines but would hide drawdown troughs here.)
      series: { dataGrouping: { ...WEEKLY_DATA_GROUPING, approximation: 'low' } },
    },
    series: [
      {
        name: 'Buy & Hold SPY',
        data: spyDrawdown,
        color: '#6c757d',
        lineWidth: 1.5,
      },
      {
        name: 'Market Health Strategy',
        data: strategyDrawdown,
        color: ZONE_COLORS.green,
        lineWidth: 2,
        zoneAxis: 'x',
        zones,
      },
    ],
  };

  return (
    <div>
      <div className={styles.rangePresets} role="group" aria-label="Drawdown chart date range">
        {RANGE_PRESETS.map((preset, index) => {
          const active = selectedPreset === index;
          return (
            <button
              key={preset.text}
              type="button"
              className={active ? styles.rangePresetButtonActive : styles.rangePresetButton}
              aria-pressed={active}
              onClick={() => handlePresetClick(preset, index)}
            >
              {preset.text}
            </button>
          );
        })}
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        callback={(chart) => { chartRef.current = chart; }}
      />
      <PerformanceStatsTable rows={drawdownStatRows} />
    </div>
  );
};

export default DrawdownChart;
